'use client';

import React from 'react';
import Link from 'next/link';
import Header from '@/components/Header';
import Footer from '@/components/Footer';

const relatedServices = [
  { label: 'Container Transport', href: '/container-transport' },
  { label: 'Less Container Load (LCL)', href: '/lcl-transport' },
  { label: 'Airfreight Transport', href: '/airfreight-transport' },
  { label: 'Inland Transport', href: '/inland-transport' },
  { label: 'Customs Representation', href: '/customs-representation' },
  { label: 'Port Operations', href: '/port-operations' },
  { label: 'Bank Guarantee', href: '/bank-guarantee' },
];

export default function CargoInsurancePage() {
  return (
    <div className="min-h-screen flex flex-col" style={{ background: '#F4F6F9' }}>
      <Header />
      <main className="flex-1 pt-32 pb-20">
        {/* Hero section */}
        <section className="max-w-6xl mx-auto px-6 lg:px-8 py-16 lg:py-24">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            {/* Illustration */}
            <div className="flex items-center justify-center">
              <svg viewBox="0 0 320 280" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full max-w-sm lg:max-w-md" aria-label="Cargo insurance shield protection illustration">
                <path d="M160 30 L240 60 L240 150 Q240 210 160 250 Q80 210 80 150 L80 60 Z" stroke="#1B6B8A" strokeWidth="4" fill="rgba(27,107,138,0.05)" strokeLinejoin="round"/>
                <path d="M160 52 L220 76 L220 152 Q220 198 160 228 Q100 198 100 152 L100 76 Z" stroke="#1B6B8A" strokeWidth="2.5" fill="rgba(27,107,138,0.03)" strokeLinejoin="round" strokeDasharray="6 3"/>
                <path d="M132 148 L150 166 L192 120" stroke="#1B6B8A" strokeWidth="5" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M56 80 L60 68 L64 80 L76 84 L64 88 L60 100 L56 88 L44 84 Z" stroke="#2A8FAB" strokeWidth="2" fill="rgba(42,143,171,0.1)"/>
                <path d="M252 100 L255 92 L258 100 L266 103 L258 106 L255 114 L252 106 L244 103 Z" stroke="#2A8FAB" strokeWidth="2" fill="rgba(42,143,171,0.1)"/>
                <path d="M270 60 L272 54 L274 60 L280 62 L274 64 L272 70 L270 64 L264 62 Z" stroke="#7EC8DC" strokeWidth="1.5" fill="rgba(126,200,220,0.1)"/>
                <rect x="120" y="240" width="80" height="30" rx="4" stroke="#2A8FAB" strokeWidth="2.5" fill="rgba(42,143,171,0.06)"/>
                <line x1="160" y1="240" x2="160" y2="270" stroke="#2A8FAB" strokeWidth="2"/>
                <line x1="120" y1="255" x2="200" y2="255" stroke="#2A8FAB" strokeWidth="2"/>
              </svg>
            </div>

            {/* Text */}
            <div>
              <div className="w-10 h-1 mb-5 rounded" style={{ background: '#1B6B8A' }} />
              <h1 className="text-4xl lg:text-5xl font-bold mb-6 leading-tight" style={{ color: '#0F2340', fontFamily: 'var(--font-display, sans-serif)' }}>
                Cargo insurance
              </h1>
              <div className="space-y-5 text-base lg:text-lg leading-relaxed" style={{ color: '#3A5070' }}>
                <p>World Transport Overseas offers comprehensive <strong style={{ color: '#0F2340' }}>cargo insurance solutions</strong> to protect your goods throughout their entire journey. Whether your shipment travels by sea, air, road, or rail, our insurance products provide the financial protection you need against loss, damage, or theft.</p>
                <p>We work with leading marine insurers to provide competitive premiums and broad coverage tailored to your specific cargo type and trade lane. From standard Institute Cargo Clauses to bespoke project cargo policies, our insurance specialists will recommend the most appropriate cover for your needs.</p>
                <p>In the event of a claim, our dedicated claims handling team acts swiftly to minimise disruption to your business. We manage the entire claims process on your behalf — from initial notification through to final settlement — ensuring you receive fair and prompt compensation.</p>
              </div>
              <div className="mt-8 flex flex-wrap gap-4">
                <Link href="/transport-quote" className="inline-flex items-center gap-2 px-7 py-3 rounded-full text-white font-semibold text-sm transition-all duration-200 hover:opacity-90 shadow-md" style={{ background: '#1A5FAB' }}>
                  Get a Quote
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4"><path d="M5 12h14M13 5l7 7-7 7" /></svg>
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* Key highlights */}
        <section className="py-14" style={{ background: '#0F2340' }}>
          <div className="max-w-6xl mx-auto px-6 lg:px-8">
            <div className="w-10 h-1 mb-4 rounded" style={{ background: '#1B6B8A' }} />
            <h2 className="text-2xl lg:text-3xl font-bold mb-10" style={{ color: '#FFFFFF' }}>Insurance Benefits</h2>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {[
                { title: 'All-Risk Cover', desc: 'Comprehensive protection against physical loss or damage during transit by any mode' },
                { title: 'All Transport Modes', desc: 'Single policy covering sea, air, road, and rail shipments worldwide' },
                { title: 'Fast Claims', desc: 'Dedicated claims handling team ensuring swift resolution and payment' },
                { title: 'Competitive Rates', desc: 'Access to leading marine insurers ensuring best-value premiums for your cargo' },
              ]?.map((h, i) => (
                <div key={i} className="rounded-2xl p-6" style={{ background: '#132848', border: '1px solid rgba(255,255,255,0.1)' }}>
                  <div className="w-8 h-8 rounded-lg mb-4 flex items-center justify-center" style={{ background: 'rgba(126,200,220,0.2)' }}>
                    <svg viewBox="0 0 24 24" fill="none" stroke="#1B6B8A" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4"><path d="M9 12l2 2 4-4M21 12c0 4.97-4.03 9-9 9s-9-4.03-9-9 4.03-9 9-9c1.66 0 3.21.45 4.55 1.23" /></svg>
                  </div>
                  <p className="font-bold text-base mb-1" style={{ color: '#FFFFFF' }}>{h?.title}</p>
                  <p className="text-sm leading-relaxed" style={{ color: '#B8CDE0' }}>{h?.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Cover types */}
        <section className="py-14" style={{ background: '#0A1628' }}>
          <div className="max-w-6xl mx-auto px-6 lg:px-8">
            <div className="w-10 h-1 mb-4 rounded" style={{ background: '#1B6B8A' }} />
            <h2 className="text-2xl lg:text-3xl font-bold mb-10" style={{ color: '#FFFFFF' }}>Cover Types</h2>
            <div className="grid sm:grid-cols-2 gap-6">
              {[
                { title: 'Institute Cargo Clauses A', desc: 'Broadest all-risks cover for general cargo — protects against all risks of physical loss or damage except specific exclusions.' },
                { title: 'Institute Cargo Clauses B', desc: 'Named perils cover including fire, explosion, vessel stranding, collision, earthquake, and water damage.' },
                { title: 'Institute Cargo Clauses C', desc: 'Basic named perils cover for lower-value cargo — fire, explosion, vessel stranding, collision, and general average.' },
                { title: 'Refrigerated Cargo Cover', desc: 'Specialist cover for temperature-sensitive goods including breakdown of refrigeration machinery.' },
                { title: 'Open Cover Policy', desc: 'Continuous insurance for regular shippers — automatic cover for all shipments under agreed terms.' },
                { title: 'Project Cargo Insurance', desc: 'Bespoke cover for high-value, oversized, or complex project cargo requiring specialist underwriting.' },
              ]?.map((c, i) => (
                <div key={i} className="rounded-2xl p-6" style={{ background: '#132848', border: '1px solid rgba(255,255,255,0.1)' }}>
                  <p className="font-bold text-base mb-2" style={{ color: '#FFFFFF' }}>{c?.title}</p>
                  <p className="text-sm leading-relaxed" style={{ color: '#B8CDE0' }}>{c?.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Related services */}
        <section className="py-14" style={{ background: '#0E1F38' }}>
          <div className="max-w-6xl mx-auto px-6 lg:px-8">
            <div className="w-10 h-1 mb-4 rounded" style={{ background: '#1B6B8A' }} />
            <h2 className="text-2xl lg:text-3xl font-bold mb-8" style={{ color: '#FFFFFF' }}>Other Services</h2>
            <div className="flex flex-wrap gap-3">
              {relatedServices?.map((s) => (
                <Link key={s?.href} href={s?.href} className="px-5 py-2.5 rounded-full text-sm font-medium transition-all duration-200 hover:opacity-80" style={{ background: '#132848', color: '#B8CDE0', border: '1px solid rgba(255,255,255,0.15)' }}>
                  {s?.label}
                </Link>
              ))}
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
